import { ProductPageProvider } from "@/components";
import { Alert, Button, Form, Input, Modal, Spin } from "antd";
import type { AxiosRequestConfig } from "axios";
import axios from "axios";
import { Building2Icon, CheckIcon, FileTextIcon, LockIcon, LogInIcon, MailIcon, ShieldCheckIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { PortalBrand } from "../components/PortalProduct";
import { portalRequest, refreshPortalSession, setPortalSession } from "../lib/auth";
import { getPortalLandingPath } from "../lib/routes";

type LoginCredentials = {
  email: string;
  password: string;
};

type PortalTenantOption = {
  slug: string;
  name: string;
  image?: string | null;
  domain?: string | null;
};

type PortalLoginSession = {
  token: string;
  actorType: "agency_user";
  actor: {
    type: "agency_user";
    id: string;
    fullname: string;
    email: string;
    agencyId: string;
    mustChangePassword?: boolean;
  };
  organisation: { name: string; image?: string | null };
  portal: { slug: string };
};

type GlobalPortalLoginResponse =
  | PortalLoginSession
  | {
      selectionRequired: true;
      tenants: PortalTenantOption[];
    };

type PortalSessionResponse = {
  actorType: "tenant_user" | "agency_user";
  portal?: { slug: string } | null;
};

type PortalAxiosRequestConfig = AxiosRequestConfig & { _skipAuthRefresh?: boolean };

const getPortalLoginError = (error: unknown) => {
  if (!axios.isAxiosError<{ error?: string }>(error)) return "Unable to sign in";
  return error.response?.data?.error || "Unable to sign in";
};

const isCompletedLogin = (response: GlobalPortalLoginResponse): response is PortalLoginSession => "token" in response;

/** Renders the global TripboxCRM agency-user login and tenant selection flow. */
const GlobalPortalLoginPage = () => {
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantOptions, setTenantOptions] = useState<PortalTenantOption[]>([]);
  const [selectedTenantSlug, setSelectedTenantSlug] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState<LoginCredentials | null>(null);

  useEffect(() => {
    let cancelled = false;
    refreshPortalSession()
      .then(() => portalRequest<PortalSessionResponse>({ url: "/api/agent-portal/session", method: "get" }))
      .then((response) => {
        const slug = response.data.portal?.slug;
        if (!cancelled && slug) {
          navigate(getPortalLandingPath(response.data.actorType), { replace: true });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const completeLogin = (session: PortalLoginSession) => {
    setPortalSession(session.token, session.actor);
    navigate(getPortalLandingPath(session.actorType));
  };

  const submitCredentials = async (credentials: LoginCredentials, tenantSlug?: string) => {
    const response = await axios.post<GlobalPortalLoginResponse>(
      "/api/agent-portal/auth/login",
      { ...credentials, ...(tenantSlug ? { tenantSlug } : {}) },
      { withCredentials: true, _skipAuthRefresh: true } as PortalAxiosRequestConfig,
    );
    if (isCompletedLogin(response.data)) {
      completeLogin(response.data);
      return;
    }
    setPendingCredentials(credentials);
    setTenantOptions(response.data.tenants);
    setSelectedTenantSlug(response.data.tenants[0]?.slug || "");
  };

  if (checkingSession) {
    return (
      <ProductPageProvider>
        <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB]">
          <Spin size="large" />
        </main>
      </ProductPageProvider>
    );
  }

  return (
    <ProductPageProvider>
      <main className="min-h-[100dvh] bg-(--portal-canvas) text-[#131B2A]">
        <div className="grid min-h-[100dvh] lg:grid-cols-[minmax(0,1fr)_480px]">
          <section className="flex flex-col bg-(--brand-secondary) px-5 py-8 text-white sm:px-8 lg:px-14 lg:py-12">
            <nav aria-label="Partner portal navigation">
              <span className="inline-flex rounded-lg bg-white px-2 py-0.5">
                <PortalBrand name="Partner portal" compact navbar />
              </span>
            </nav>
            <div className="my-auto max-w-2xl py-10">
              <span className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-semibold text-(--portal-accent)">
                <ShieldCheckIcon size={14} strokeWidth={1.8} /> Secure agent access
              </span>
              <h1 className="portal-title mt-6 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
                Your agency workspaces, one secure login.
              </h1>
              <p className="mt-5 max-w-xl text-sm leading-7 text-white/70 sm:text-base">
                Sign in once to access itineraries, live quotes, and every travel partner that works with your agency.
              </p>
              <div className="mt-8 grid max-w-xl gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <Building2Icon size={17} className="text-(--portal-accent)" strokeWidth={1.8} />
                  <p className="mt-3 text-sm font-semibold text-white">Partner-aware access</p>
                  <p className="mt-1 text-xs leading-5 text-white/60">
                    Choose the right workspace when your agency serves more than one travel partner.
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <FileTextIcon size={17} className="text-(--portal-accent)" strokeWidth={1.8} />
                  <p className="mt-3 text-sm font-semibold text-white">One itinerary desk</p>
                  <p className="mt-1 text-xs leading-5 text-white/60">
                    Move between partner workspaces without signing in again.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <aside className="flex items-center bg-(--portal-canvas) px-5 py-10 sm:px-8 lg:px-12">
            <div className="w-full">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-(--brand-primary)">Agent portal</p>
              <h2 className="portal-title mt-2 text-3xl font-semibold text-[#131B2A]">Welcome back</h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Use the email and password provided for your agency account.
              </p>

              <Form<LoginCredentials>
                className="mt-8"
                layout="vertical"
                requiredMark="optional"
                onFinish={async (values) => {
                  try {
                    setSubmitting(true);
                    setError(null);
                    await submitCredentials(values);
                  } catch (requestError: unknown) {
                    setError(getPortalLoginError(requestError));
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {error ? <Alert className="mb-5" type="error" showIcon message={error} /> : null}
                <Form.Item
                  label="Email address"
                  name="email"
                  rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
                >
                  <Input
                    prefix={<MailIcon size={15} className="text-gray-400" />}
                    autoComplete="email"
                    placeholder="name@company.com"
                  />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[{ required: true, message: "Enter your password" }]}
                >
                  <Input.Password
                    prefix={<LockIcon size={15} className="text-gray-400" />}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit" block icon={<LogInIcon size={16} />} loading={submitting}>
                  Sign in
                </Button>
              </Form>
              <p className="mt-5 border-t border-gray-200 pt-5 text-xs leading-5 text-gray-500">
                Need password help? Open your travel partner&apos;s portal link to request a reset.
              </p>
            </div>
          </aside>
        </div>

        <Modal
          title="Choose a partner workspace"
          open={tenantOptions.length > 0}
          closable={!submitting}
          maskClosable={false}
          onCancel={() => {
            setTenantOptions([]);
            setSelectedTenantSlug("");
            setPendingCredentials(null);
          }}
          footer={[
            <Button
              key="cancel"
              disabled={submitting}
              onClick={() => {
                setTenantOptions([]);
                setSelectedTenantSlug("");
                setPendingCredentials(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="continue"
              type="primary"
              loading={submitting}
              disabled={!selectedTenantSlug || !pendingCredentials}
              onClick={async () => {
                if (!pendingCredentials || !selectedTenantSlug) return;
                try {
                  setSubmitting(true);
                  setError(null);
                  await submitCredentials(pendingCredentials, selectedTenantSlug);
                } catch (requestError: unknown) {
                  setTenantOptions([]);
                  setError(getPortalLoginError(requestError));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Continue
            </Button>,
          ]}
        >
          <p className="mb-4 text-sm leading-6 text-gray-600">
            Your credentials match more than one travel partner. Select the workspace you want to open.
          </p>
          <div className="grid gap-2" role="listbox" aria-label="Available partner workspaces">
            {tenantOptions.map((tenant) => {
              const selected = selectedTenantSlug === tenant.slug;
              return (
                <button
                  key={tenant.slug}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => setSelectedTenantSlug(tenant.slug)}
                  className={[
                    "flex w-full items-center justify-between rounded-xl border p-3 text-left",
                    "transition-[border-color,background-color,transform] duration-150 focus-visible:outline-none",
                    "focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 active:scale-[0.99]",
                    selected
                      ? "border-(--brand-primary) bg-(--brand-primary)/10"
                      : "border-gray-200 bg-white hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB]",
                  ].join(" ")}
                >
                  <PortalBrand name={tenant.name} image={tenant.image} compact />
                  <span
                    className={[
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
                      selected
                        ? "border-(--brand-primary) bg-(--brand-primary) text-[var(--brand-primary-foreground)]"
                        : "border-gray-300 text-transparent",
                    ].join(" ")}
                  >
                    <CheckIcon size={14} strokeWidth={2} />
                  </span>
                </button>
              );
            })}
          </div>
        </Modal>
      </main>
    </ProductPageProvider>
  );
};

export default GlobalPortalLoginPage;
